import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import moment from 'moment';
import { GraphClockService } from '../graph-clock';
import { OverlayBackStackService } from '../../../services/overlay-back-stack.service';
import { comparePoints, formatSpan, GraphPoint, PointComparison, pointWhat, projectClose, rands } from '../graph-point';

/**
 * The 7R graph's point drawer (OE-265, Paul: "a side drawer that can be closed; clicking once and then
 * again can show the comparison and insights"). It slides in from the right edge of the page (a
 * bottom sheet on a phone), so it never covers the point it describes. One point: its values and
 * where it stood against the target line. Two points: what each line added between them, the rate,
 * the target over the same span and where the period closes at that rate. A third click starts over.
 */
@Component({
    selector: 'shared-point-drawer',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <aside class="point-drawer" [class.open]="points.length > 0" role="complementary" aria-label="Point details">
            <ng-container *ngIf="points.length">
                <div class="pd-grab" aria-hidden="true"></div>
                <header class="pd-head">
                    <h3>{{ points.length === 2 ? 'Comparing two points' : 'Point details' }}</h3>
                    <button
                        type="button"
                        class="pd-close"
                        aria-label="Close"
                        title="Close (Esc)"
                        (click)="closed.emit()"
                    >
                        &times;
                    </button>
                </header>

                <div class="pd-body">
                    <section
                        *ngFor="let p of shown; let i = index"
                        class="pd-point"
                        [attr.aria-label]="(points.length === 2 ? (i === 0 ? 'From ' : 'To ') : '') + timeOf(p)"
                    >
                        <div class="pd-time">
                            <span *ngIf="points.length === 2" class="pd-tag">{{ i === 0 ? 'A' : 'B' }}</span>
                            {{ timeOf(p) }}
                        </div>
                        <div class="pd-what" *ngIf="whatOf(p) as w">{{ w }}</div>
                        <div class="pd-row" *ngFor="let l of p.lines">
                            <span class="pd-dot" [style.background]="l.colour"></span>
                            <span class="pd-name">{{ l.name }}</span>
                            <span class="pd-value">{{ r(l.accumulated) }}</span>
                            <span class="pd-detail">{{ detailOf(l) }}</span>
                        </div>
                        <p class="pd-note" *ngIf="vsLine(p) as v" [class.good]="v.ahead" [class.bad]="!v.ahead">
                            {{ v.text }}
                        </p>
                    </section>

                    <section class="pd-compare" *ngIf="comparison as c" aria-label="Between the two points">
                        <h4>Between A and B &middot; {{ span(c) }}</h4>
                        <div class="pd-row" *ngFor="let a of c.added">
                            <span class="pd-dot" [style.background]="a.colour"></span>
                            <span class="pd-name">{{ a.name }}</span>
                            <span class="pd-value">{{ signed(a.amount) }}</span>
                        </div>
                        <ul class="pd-insights">
                            <li *ngIf="c.rate !== null">
                                <strong>{{ r(c.rate) }}</strong> an {{ c.rateUnit === 'hour' ? 'hour' : 'average day' }}
                                over this stretch.
                            </li>
                            <li
                                *ngIf="c.versusTarget !== undefined"
                                [class.good]="c.versusTarget >= 0"
                                [class.bad]="c.versusTarget < 0"
                            >
                                The target asked for {{ r(c.targetNeeded || 0) }} over the same time:
                                {{ c.versusTarget >= 0 ? 'beat it by' : 'short by' }} {{ r(abs(c.versusTarget)) }}.
                            </li>
                            <li *ngIf="projected !== null">
                                At this rate the {{ c.rateUnit === 'hour' ? 'day' : 'month' }} closes near
                                <strong>{{ r(projected) }}</strong
                                >.
                            </li>
                        </ul>
                    </section>

                    <p class="pd-hint">
                        {{
                            points.length === 1
                                ? 'Click another point on the graph to compare the two.'
                                : 'Click the graph again to start a new comparison.'
                        }}
                    </p>
                </div>
            </ng-container>
        </aside>
    `,
    styles: [
        `
            :host {
                display: contents;
            }
            .point-drawer {
                position: fixed;
                top: 0;
                right: 0;
                bottom: 0;
                z-index: 1001; /* over the CDK overlays and the floating buttons */
                width: min(360px, 100vw);
                display: flex;
                flex-direction: column;
                background: var(--pd-bg, #fff);
                color: var(--pd-fg, #1f2328);
                box-shadow: -6px 0 24px rgba(0, 0, 0, 0.18);
                transform: translateX(105%);
                transition: transform 0.22s ease;
                font:
                    13px/1.4 Roboto,
                    'Helvetica Neue',
                    sans-serif;
            }
            .point-drawer.open {
                transform: none;
            }
            .pd-grab {
                display: none;
            }
            .pd-head {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: calc(16px + var(--ng-top-inset, env(safe-area-inset-top, 0px))) 12px 10px 16px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            }
            .pd-head h3 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
            }
            .pd-close {
                width: 36px;
                height: 36px;
                border: 0;
                border-radius: 50%;
                background: transparent;
                color: inherit;
                font-size: 22px;
                line-height: 36px;
                cursor: pointer;
            }
            .pd-close:hover {
                background: rgba(0, 0, 0, 0.06);
            }
            .pd-body {
                flex: 1 1 auto;
                min-height: 0;
                overflow-y: auto;
                padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
                display: flex;
                flex-direction: column;
                gap: 12px;
            }
            .pd-point,
            .pd-compare {
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-radius: 10px;
                padding: 10px 12px;
            }
            .pd-time {
                font-weight: 600;
                margin-bottom: 6px;
                display: flex;
                align-items: center;
                gap: 8px;
            }
            .pd-tag {
                display: inline-grid;
                place-items: center;
                width: 20px;
                height: 20px;
                border-radius: 50%;
                background: #00897b;
                color: #fff;
                font-size: 11px;
            }
            .pd-what {
                margin: -2px 0 6px;
                color: rgba(0, 0, 0, 0.6);
                font-size: 12px;
            }
            .pd-row {
                display: grid;
                grid-template-columns: 12px 1fr auto;
                column-gap: 8px;
                align-items: center;
                padding: 2px 0;
            }
            .pd-dot {
                width: 10px;
                height: 10px;
                border-radius: 50%;
            }
            .pd-value {
                font-weight: 600;
                font-variant-numeric: tabular-nums;
            }
            .pd-detail {
                grid-column: 2 / 4;
                color: rgba(0, 0, 0, 0.6);
                font-size: 12px;
            }
            .pd-note,
            .pd-hint {
                margin: 8px 0 0;
                font-size: 12px;
            }
            .pd-hint {
                color: rgba(0, 0, 0, 0.55);
                text-align: center;
            }
            .pd-compare h4 {
                margin: 0 0 6px;
                font-size: 13px;
                font-weight: 600;
            }
            .pd-insights {
                margin: 8px 0 0;
                padding-left: 18px;
            }
            .pd-insights li {
                margin: 4px 0;
            }
            .good {
                color: #00796b;
            }
            .bad {
                color: #c62828;
            }
            :host-context(.dark-theme) .point-drawer {
                --pd-bg: #2b2f33;
                --pd-fg: #e8eaed;
            }
            :host-context(.dark-theme) .pd-what,
            :host-context(.dark-theme) .pd-detail,
            :host-context(.dark-theme) .pd-hint {
                color: rgba(255, 255, 255, 0.6);
            }
            :host-context(.dark-theme) .good {
                color: #4db6ac;
            }
            :host-context(.dark-theme) .bad {
                color: #ef9a9a;
            }
            /* Phones: a bottom sheet, per DESIGN.md Sheets. */
            @media (max-width: 767.98px) {
                .point-drawer {
                    top: auto;
                    left: 0;
                    width: 100%;
                    max-height: 70vh;
                    border-radius: 16px 16px 0 0;
                    box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.18);
                    transform: translateY(105%);
                }
                .pd-grab {
                    display: block;
                    width: 36px;
                    height: 4px;
                    border-radius: 2px;
                    background: rgba(0, 0, 0, 0.25);
                    margin: 8px auto 0;
                }
                .pd-head {
                    padding-top: 4px;
                }
            }
        `,
    ],
})
export class PointDrawerComponent implements AfterViewInit, OnChanges, OnDestroy {
    /** No points = closed; one = its details; two = the comparison. */
    @Input() public points: GraphPoint[] = [];
    @Output() public closed = new EventEmitter<void>();

    public shown: GraphPoint[] = [];
    public comparison: PointComparison | null = null;
    public projected: number | null = null;
    private releaseBack?: () => void;
    private readonly clock = inject(GraphClockService);

    constructor(
        private readonly backStack: OverlayBackStackService,
        private readonly host: ElementRef<HTMLElement>
    ) {}

    /**
     * Lives on <body>, like Material's overlays: inside the page it shares the content pane's stacking
     * context, where the app header and the phone bottom bar paint over it whatever its z-index.
     */
    public ngAfterViewInit(): void {
        document.body.appendChild(this.host.nativeElement);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes['points']) return;
        const pts = this.points || [];
        this.comparison = pts.length === 2 ? comparePoints(pts[0], pts[1]) : null;
        this.shown = this.comparison ? [this.comparison.from, this.comparison.to] : pts;
        this.projected = this.comparison ? projectClose(this.comparison) : null;
        // System Back closes the drawer (one layer per press, OverlayBackStackService).
        if (pts.length && !this.releaseBack) this.releaseBack = this.backStack.push(() => this.closed.emit());
        if (!pts.length) this.release();
    }

    public ngOnDestroy(): void {
        this.release();
        this.host.nativeElement.remove();
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        if (this.points?.length) this.closed.emit();
    }

    private release(): void {
        const release = this.releaseBack;
        this.releaseBack = undefined;
        release?.();
    }

    public r = rands;
    public abs = Math.abs;

    public signed(n: number): string {
        return (n > 0 ? '+' : '') + rands(n);
    }

    public timeOf(p: GraphPoint): string {
        return moment(this.clock.show(p.date)).format(p.kind === 'daily' ? 'dddd HH:mm' : 'dddd D MMMM');
    }

    public span(c: PointComparison): string {
        return formatSpan(c.spanMs, c.to.kind);
    }

    /** What this point was: the transaction and who made it (the same for every line). */
    public whatOf(p: GraphPoint): string {
        return pointWhat(p);
    }

    /** What this line added at the point. */
    public detailOf(l: GraphPoint['lines'][number]): string {
        return l.value ? `${this.signed(l.value)} here` : 'no change here';
    }

    /** Where the Total stood against the target line at that moment. */
    public vsLine(p: GraphPoint): { text: string; ahead: boolean } | null {
        if (p.targetAt === undefined) return null;
        const total = p.lines.find((l) => l.line === 'primary')?.accumulated;
        if (total === undefined) return null;
        const gap = total - p.targetAt;
        return {
            ahead: gap >= 0,
            text: `${rands(Math.abs(gap))} ${gap >= 0 ? 'ahead of' : 'behind'} the target line (${rands(p.targetAt)}).`,
        };
    }
}
